import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatCount } from '../mock';
import { toast } from 'sonner';
import {
  ShieldAlert, Trash2, Users, Film, DollarSign, MessageSquare, Loader2, Crown,
  Activity, Key, FileText, Bell, Search, Ban, CheckCircle2, AlertTriangle, Music,
  BookOpen, Eye, PieChart, BarChart3, Download, Copy, Plus, RefreshCw, X, Clock, Edit3,
} from 'lucide-react';
import {
  adminStats, listVideos, deleteVideo, updateAnnouncement, getAnnouncement, resolveFileUrl,
  adminListUsers, adminToggleBan, adminCleanupTestUsers, adminModerationRecent,
  adminDeleteTrack, adminDeleteHub, adminAnalytics, adminAudit, adminToursCsvUrl,
  ownerListKeys, ownerCreateKey, ownerUpdateKey, ownerDeleteKey, adminListKeys,
} from '../lib/api';
import RoleBadge from '../components/RoleBadge';
import AdminUserEditor from '../components/AdminUserEditor';
import AdminPlansTab from '../components/AdminPlansTab';

// ——————————————————————————————————————————————————————————————
// Tabbed admin operational dashboard, organized into 3 logical groups:
//   Operations       — Overview / Users / Moderation / Audit
//   Commerce         — Plans (subscriptions + ad packages + MRR)
//   Communications   — Secrets / Announce
// ——————————————————————————————————————————————————————————————

const TAB_GROUPS = [
  { id: 'ops', label: 'Operations', tabs: [
    { id: 'overview',   label: 'Overview',    icon: Activity },
    { id: 'users',      label: 'Users',       icon: Users },
    { id: 'moderation', label: 'Moderation',  icon: ShieldAlert },
    { id: 'audit',      label: 'Audit log',   icon: FileText },
  ]},
  { id: 'commerce', label: 'Commerce', tabs: [
    { id: 'plans',      label: 'Plans & MRR', icon: DollarSign },
  ]},
  { id: 'comms', label: 'Communications', tabs: [
    { id: 'secrets',    label: 'Secrets',     icon: Key },
    { id: 'announce',   label: 'Announce',    icon: Bell },
  ]},
];

export default function Admin() {
  const { user } = useApp();
  const [tab, setTab] = useState('overview');

  if (user && user.role !== 'admin' && user.role !== 'owner') {
    return (
      <div className="py-10">
        <div className="card-shell p-6 max-w-[560px]">
          <div className="flex items-center gap-2 text-[#ff6b8a]"><ShieldAlert className="w-5 h-5" /> <span className="font-semibold">Admins only</span></div>
          <p className="text-white/65 mt-2 text-[14px]">Sign in as Admin or Owner to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="max-w-[1280px] mx-auto">
      <div className="flex flex-wrap items-center gap-3">
        <RoleBadge role={user?.role || 'admin'} />
        <span className="text-white/45 text-[12px]">{user?.role === 'owner' ? 'Full platform control' : 'Content moderation + ops'}</span>
      </div>
      <h1 className="font-display text-[28px] sm:text-[32px] md:text-[40px] mt-2 leading-none">Operations dashboard</h1>
      <p className="text-white/60 text-[13.5px] md:text-[14px] mt-1 max-w-[760px]">Every admin action is logged. Be deliberate — bans and content deletions are visible in the Audit tab.</p>

      {user?.role === 'owner' && (
        <div className="card-shell p-3 md:p-4 mt-5 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: 'rgba(255,207,82,0.35)', background: 'linear-gradient(90deg, rgba(255,207,82,0.08), rgba(255,207,82,0.02))' }}>
          <div className="flex items-center gap-2 text-[13px]"><Crown className="w-4 h-4 text-[#ffcf52]" /> Signed in as Owner. Platform settings live on the Owner dashboard.</div>
          <Link to="/owner" className="btn-ghost text-[12.5px]"><Crown className="w-4 h-4 text-[#ffcf52]" /> Owner dashboard</Link>
        </div>
      )}

      {/* Grouped tab strips */}
      <div className="mt-5 sticky top-14 bg-[var(--theme-bg)]/90 backdrop-blur z-10 py-2 -mx-4 md:mx-0 px-4 md:px-0 space-y-2">
        {TAB_GROUPS.map(grp => (
          <div key={grp.id} className="flex items-center flex-wrap gap-1.5">
            <span className="text-[10.5px] uppercase tracking-wider font-bold text-white/40 mr-1">{grp.label}</span>
            {grp.tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                      className={`chip ${tab === t.id ? 'active' : ''}`}
                      data-testid={`admin-tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5 mr-1 inline" /> {t.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-5">
        {tab === 'overview'   && <OverviewTab isOwner={user?.role === 'owner'} />}
        {tab === 'users'      && <UsersTab currentUser={user} isOwner={user?.role === 'owner'} />}
        {tab === 'moderation' && <ModerationTab />}
        {tab === 'plans'      && <AdminPlansTab isOwner={user?.role === 'owner'} />}
        {tab === 'secrets'    && <SecretsTab isOwner={user?.role === 'owner'} />}
        {tab === 'audit'      && <AuditTab />}
        {tab === 'announce'   && <AnnounceTab />}
      </div>
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function OverviewTab({ isOwner }) {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try { const [s, a] = await Promise.all([adminStats(), adminAnalytics(30)]); setStats(s); setAnalytics(a); }
    catch { toast.error('Could not load stats'); }
    setLoading(false);
  })(); }, []);

  if (loading) return <LoaderBlock />;

  const cards = [
    { ic: Users,    c: '#22d3ee', label: 'Users',       value: analytics?.users_count || 0 },
    { ic: Film,     c: '#ff2e63', label: 'Videos',      value: analytics?.videos_count || 0 },
    { ic: Music,    c: '#a78bfa', label: 'Tracks',      value: analytics?.tracks_count || 0 },
    { ic: BookOpen, c: '#22d3ee', label: 'Hub items',   value: analytics?.hub_items_count || 0 },
    { ic: Eye,      c: '#ffcf52', label: 'Total views', value: formatCount(stats?.views || 0) },
    { ic: DollarSign, c: '#34d399', label: 'Hub revenue', value: '$' + ((analytics?.hub_revenue_cents || 0) / 100).toFixed(2) },
  ];

  const maxBar = Math.max(1, ...(analytics?.signups_by_day || []).map(d => d.count));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((s, i) => (
          <div key={i} className="card-shell p-4">
            <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: s.c + '22', border: '1px solid ' + s.c + '55' }}>
              <s.ic className="w-4 h-4" style={{ color: s.c }} />
            </div>
            <div className="font-display text-[22px] md:text-[26px] mt-2 leading-none">{s.value}</div>
            <div className="text-[11px] text-white/55 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Signups chart */}
      <div className="card-shell p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#a78bfa]" /><h3 className="font-display text-[18px]">Signups · last 30 days</h3></div>
          <div className="flex items-center gap-3 text-[12px] text-white/60">
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#22d3ee] mr-1 align-middle"></span>{analytics?.signups_by_profile?.listener || 0} listeners</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#a78bfa] mr-1 align-middle"></span>{analytics?.signups_by_profile?.musician || 0} musicians</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-[#ff2e63] mr-1 align-middle"></span>{analytics?.signups_by_profile?.creator || 0} creators</span>
          </div>
        </div>
        {analytics?.signups_by_day?.length ? (
          <div className="flex items-end gap-1 h-28 md:h-32">
            {analytics.signups_by_day.map(d => (
              <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center gap-1" title={`${d.date} · ${d.count}`}>
                <div className="w-full rounded-t" style={{ height: `${(d.count / maxBar) * 100}%`, minHeight: '2px', background: 'linear-gradient(180deg,#a78bfa,#22d3ee)' }} />
                <div className="text-[9px] text-white/35 hidden md:block">{d.date.slice(-2)}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-white/50 text-[13px] py-6 text-center">No signups in the window.</div>}
      </div>

      {/* Funnel + engagement metrics */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Tour leads captured" value={analytics?.tour_leads_total} sub={`${analytics?.tour_leads_completed || 0} completed`} color="#a78bfa" />
        <MetricCard label="Tour conversion" value={`${analytics?.tour_conversion_pct || 0}%`} sub={`${analytics?.tour_leads_completed || 0} / ${analytics?.tour_leads_total || 0}`} color="#22d3ee" />
        <MetricCard label="Academy graduates" value={analytics?.academy_users_completed_any} sub="Users completing ≥1 track" color="#ffcf52" />
        <MetricCard label="Track engagement" value={formatCount(analytics?.tracks_total_plays || 0)} sub={`${formatCount(analytics?.tracks_total_likes || 0)} likes`} color="#ff2e63" />
      </div>

      {isOwner && (
        <div className="card-shell p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-[17px]">DB hygiene</h3>
              <p className="text-white/55 text-[13px] mt-1 max-w-[500px]">Bulk-delete leftover test accounts (emails matching <code className="text-[#a78bfa]">test_*</code>, <code className="text-[#a78bfa]">@test.com</code>, <code className="text-[#a78bfa]">@example.com</code>, etc.) and all their content.</p>
            </div>
            <CleanupTestsButton />
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div className="card-shell p-4" style={{ borderColor: color + '44' }}>
      <div className="text-[11px] uppercase tracking-wider text-white/45">{label}</div>
      <div className="font-display text-[26px] mt-1 leading-none" style={{ color }}>{value ?? '—'}</div>
      {sub && <div className="text-[11.5px] text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

function CleanupTestsButton() {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await adminCleanupTestUsers(); toast.success(`Removed ${r.removed} test account${r.removed === 1 ? '' : 's'}`); setConfirming(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cleanup failed'); }
    finally { setBusy(false); }
  };
  if (!confirming) return <button onClick={() => setConfirming(true)} className="btn-ghost text-[12.5px]" data-testid="admin-cleanup-tests"><Trash2 className="w-3.5 h-3.5" /> Clean test accounts</button>;
  return (
    <div className="flex items-center gap-2">
      <button disabled={busy} onClick={run} className="btn-primary text-[12.5px] disabled:opacity-60" data-testid="admin-cleanup-confirm">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />} Confirm — cannot undo
      </button>
      <button onClick={() => setConfirming(false)} className="btn-ghost text-[12.5px]">Cancel</button>
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function UsersTab({ currentUser, isOwner }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [pt, setPt] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const limit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListUsers({ q: q || undefined, role: role || undefined, profile_type: pt || undefined, skip, limit });
      setRows(r.items); setTotal(r.total);
    } catch { toast.error('Could not load users'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, role, pt]);
  const search = () => { setSkip(0); load(); };

  const toggleBan = async (u) => {
    try {
      const r = await adminToggleBan(u.id, !u.banned);
      setRows(list => list.map(x => x.id === u.id ? { ...x, banned: r.banned } : x));
      toast.success(r.banned ? `Banned ${u.email}` : `Unbanned ${u.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div>
      <div className="card-shell p-3 md:p-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 flex-1 min-w-[200px] focus-within:border-[#a78bfa]/60">
          <Search className="w-3.5 h-3.5 text-white/50" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} placeholder="Search email or name…" className="bg-transparent flex-1 focus:outline-none text-[13px] placeholder:text-white/40" data-testid="admin-users-search" />
          <button onClick={search} className="text-[11px] text-white/70 hover:text-white">Search</button>
        </div>
        <select value={role} onChange={e => { setRole(e.target.value); setSkip(0); }} className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 text-[12.5px] focus:outline-none" data-testid="admin-users-role">
          <option value="">All roles</option>
          <option value="creator">Creator</option>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
        </select>
        <select value={pt} onChange={e => { setPt(e.target.value); setSkip(0); }} className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 text-[12.5px] focus:outline-none" data-testid="admin-users-profile">
          <option value="">All types</option>
          <option value="creator">Creator</option>
          <option value="musician">Musician</option>
          <option value="listener">Listener</option>
          <option value="both">Both</option>
        </select>
        <button onClick={load} className="btn-ghost text-[12.5px]"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <div className="mt-3 text-[12.5px] text-white/55">{total} matching user{total === 1 ? '' : 's'} · showing {rows.length}</div>

      <div className="card-shell mt-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white/[0.03] text-white/55 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">User</th>
                <th className="text-left px-3 py-2.5 font-semibold hidden md:table-cell">Role</th>
                <th className="text-left px-3 py-2.5 font-semibold hidden lg:table-cell">Profile</th>
                <th className="text-left px-3 py-2.5 font-semibold hidden lg:table-cell">Tier</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : rows.length ? rows.map(u => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-${u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold truncate max-w-[220px]">{u.name || '—'}</div>
                    <div className="text-[11.5px] text-white/50 truncate max-w-[220px]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell capitalize">{u.role}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell capitalize text-white/75">{u.profile_type}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell uppercase text-[11px] text-white/60">{u.reapercast_tier}</td>
                  <td className="px-3 py-2.5">
                    {u.banned ? <span className="chip text-[10px] uppercase" style={{ background: 'rgba(255,46,99,0.18)', color: '#ff6b8a', borderColor: 'rgba(255,46,99,0.4)' }}><Ban className="w-3 h-3 mr-1 inline" /> Banned</span>
                             : <span className="chip text-[10px] uppercase" style={{ background: 'rgba(52,211,153,0.14)', color: '#34d399', borderColor: 'rgba(52,211,153,0.4)' }}><CheckCircle2 className="w-3 h-3 mr-1 inline" /> Active</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => setEditing(u)} className="text-[11.5px] px-2.5 py-1 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30" data-testid={`admin-edit-${u.id}`} title="Edit user">
                        <Edit3 className="w-3 h-3 inline mr-1" /> Edit
                      </button>
                      {u.role !== 'owner' && u.id !== currentUser?.id ? (
                        <button onClick={() => toggleBan(u)} className={`text-[11.5px] px-2.5 py-1 rounded-full border ${u.banned ? 'border-[#34d399]/50 text-[#34d399] hover:bg-[#34d399]/10' : 'border-[#ff2e63]/50 text-[#ff6b8a] hover:bg-[#ff2e63]/10'}`} data-testid={`admin-ban-${u.id}`}>
                          {u.banned ? 'Unban' : 'Ban'}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <button disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - limit))} className="btn-ghost text-[12px] disabled:opacity-40">Prev</button>
        <span className="text-[12px] text-white/50">Page {Math.floor(skip / limit) + 1}</span>
        <button disabled={skip + rows.length >= total} onClick={() => setSkip(s => s + limit)} className="btn-ghost text-[12px] disabled:opacity-40">Next</button>
      </div>

      {editing && <AdminUserEditor
        user={editing}
        currentUser={currentUser}
        isOwner={isOwner}
        onClose={() => setEditing(null)}
        onSaved={(u) => setRows(list => list.map(x => x.id === u.id ? { ...x, ...u } : x))}
      />}
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function ModerationTab() {
  const [data, setData] = useState({ videos: [], tracks: [], hub: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { setData(await adminModerationRecent(30)); } catch { toast.error('Load failed'); } setLoading(false); };
  useEffect(() => { load(); }, []);

  const delVideo = async (v) => { if (!window.confirm(`Delete video "${v.title}"?`)) return; try { await deleteVideo(v.id); setData(d => ({ ...d, videos: d.videos.filter(x => x.id !== v.id) })); toast.success('Video removed'); } catch { toast.error('Failed'); } };
  const delTrack = async (t) => { if (!window.confirm(`Delete track "${t.title}"?`)) return; try { await adminDeleteTrack(t.id); setData(d => ({ ...d, tracks: d.tracks.filter(x => x.id !== t.id) })); toast.success('Track removed'); } catch { toast.error('Failed'); } };
  const delHub = async (h) => { if (!window.confirm(`Delete hub item "${h.title}"?`)) return; try { await adminDeleteHub(h.id); setData(d => ({ ...d, hub: d.hub.filter(x => x.id !== h.id) })); toast.success('Hub item removed'); } catch { toast.error('Failed'); } };

  if (loading) return <LoaderBlock />;

  return (
    <div className="space-y-6">
      <Section title="Recent videos" icon={Film} count={data.videos.length} data-testid="mod-videos">
        {data.videos.length === 0 && <Empty label="No recent videos." />}
        <div className="divide-y divide-white/5">
          {data.videos.map(v => (
            <ModRow key={v.id} thumb={v.thumb} title={v.title} sub={`${v.channel_name || '—'} · ${formatCount(v.views)} views · ${(v.created_at || '').slice(0, 10)}`} onDelete={() => delVideo(v)} testid={`mod-video-${v.id}`} />
          ))}
        </div>
      </Section>

      <Section title="Recent tracks" icon={Music} count={data.tracks.length} data-testid="mod-tracks">
        {data.tracks.length === 0 && <Empty label="No recent tracks." />}
        <div className="divide-y divide-white/5">
          {data.tracks.map(t => (
            <ModRow key={t.id} thumb={t.cover_url} title={t.title} sub={`${t.artist_name || '—'} · ${t.genre || '—'} · ${t.plays} plays`} onDelete={() => delTrack(t)} testid={`mod-track-${t.id}`} square />
          ))}
        </div>
      </Section>

      <Section title="Recent hub items" icon={BookOpen} count={data.hub.length} data-testid="mod-hub">
        {data.hub.length === 0 && <Empty label="No hub items yet." />}
        <div className="divide-y divide-white/5">
          {data.hub.map(h => (
            <ModRow key={h.id} thumb={h.cover_url} title={h.title} sub={`${h.kind} · $${(h.price_cents / 100).toFixed(2)} · ${(h.created_at || '').slice(0, 10)}`} onDelete={() => delHub(h)} testid={`mod-hub-${h.id}`} square />
          ))}
        </div>
      </Section>
    </div>
  );
}

function ModRow({ thumb, title, sub, onDelete, testid, square }) {
  return (
    <div className="flex items-center gap-3 p-3" data-testid={testid}>
      <div className={`${square ? 'w-12 h-12' : 'w-20 aspect-video'} rounded bg-white/5 overflow-hidden shrink-0 grid place-items-center`}>
        {thumb ? <img src={resolveFileUrl(thumb)} alt="" className="w-full h-full object-cover" /> : <Film className="w-4 h-4 text-white/30" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13.5px] truncate">{title || '(untitled)'}</div>
        <div className="text-[12px] text-white/50 truncate">{sub}</div>
      </div>
      <button onClick={onDelete} className="p-2 rounded-md text-white/55 hover:text-[#ff6b8a] hover:bg-[#ff2e63]/10" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function Section({ title, icon: Ic, count, children, ...rest }) {
  return (
    <div className="card-shell overflow-hidden" {...rest}>
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <Ic className="w-4 h-4 text-white/60" />
        <div className="font-display text-[15.5px]">{title}</div>
        <span className="chip text-[10px] ml-auto">{count}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function SecretsTab({ isOwner }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ service: '', label: '', value: '', admin_visible: false });

  const load = async () => {
    setLoading(true);
    try { setKeys(isOwner ? await ownerListKeys() : await adminListKeys()); }
    catch { toast.error('Could not load secrets'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isOwner]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.service || !form.label || !form.value) { toast.error('All fields required'); return; }
    try { await ownerCreateKey(form); setForm({ service: '', label: '', value: '', admin_visible: false }); setAddOpen(false); await load(); toast.success('Secret stored'); }
    catch { toast.error('Failed'); }
  };
  const del = async (k) => { if (!window.confirm(`Delete "${k.service} / ${k.label}" secret?`)) return; try { await ownerDeleteKey(k.id); await load(); toast.success('Removed'); } catch { toast.error('Failed'); } };
  const toggleVisible = async (k) => { try { await ownerUpdateKey(k.id, { admin_visible: !k.admin_visible }); await load(); } catch { toast.error('Failed'); } };
  const copy = (v) => { navigator.clipboard.writeText(v || ''); toast.success('Copied'); };

  return (
    <div>
      <div className="card-shell p-4 md:p-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><Key className="w-4 h-4 text-[#22d3ee]" /><h3 className="font-display text-[18px]">API keys & secrets</h3></div>
          <p className="text-white/60 text-[13px] mt-1 max-w-[540px]">Stored encrypted-at-rest in MongoDB. Admins see only the ones marked "Admin visible". Never hardcode secrets in the codebase.</p>
        </div>
        {isOwner && (
          <button onClick={() => setAddOpen(v => !v)} className="btn-primary text-[12.5px]" data-testid="admin-add-secret"><Plus className="w-4 h-4" /> New secret</button>
        )}
      </div>

      {isOwner && addOpen && (
        <form onSubmit={create} className="card-shell p-4 md:p-5 mt-3 grid sm:grid-cols-2 gap-3" data-testid="admin-secret-form">
          <input value={form.service} onChange={e => setForm(f => ({ ...f, service: e.target.value }))} placeholder="Service (e.g. stripe, sendgrid)" className="input-field" data-testid="secret-service" />
          <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Label (e.g. prod, staging)" className="input-field" data-testid="secret-label" />
          <input type="password" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="Secret value" className="input-field sm:col-span-2" data-testid="secret-value" />
          <label className="flex items-center gap-2 text-[13px] text-white/75 cursor-pointer">
            <input type="checkbox" checked={form.admin_visible} onChange={e => setForm(f => ({ ...f, admin_visible: e.target.checked }))} />
            Share with Admins (they can use it but not edit)
          </label>
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-primary text-[12.5px]" data-testid="secret-save">Save</button>
            <button type="button" onClick={() => setAddOpen(false)} className="btn-ghost text-[12.5px]">Cancel</button>
          </div>
        </form>
      )}

      <div className="card-shell mt-4 overflow-hidden">
        {loading ? <LoaderBlock /> : keys.length === 0 ? <Empty label="No secrets stored yet." /> : (
          <div className="divide-y divide-white/5">
            {keys.map(k => (
              <div key={k.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-secret-${k.id}`}>
                <div className="w-10 h-10 rounded-lg grid place-items-center bg-white/5 border border-white/10 shrink-0"><Key className="w-4 h-4 text-[#22d3ee]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold uppercase text-[12px] tracking-wider text-[#a78bfa]">{k.service}</span>
                    <span className="text-white/75 text-[13.5px]">{k.label}</span>
                    {k.admin_visible && <span className="chip text-[10px]" style={{ background: 'rgba(34,211,238,0.12)', color: '#22d3ee', borderColor: 'rgba(34,211,238,0.4)' }}>Admin-visible</span>}
                  </div>
                  <code className="text-[11.5px] text-white/50 block mt-1 truncate">{k.value || '••••••••'}</code>
                </div>
                <div className="flex items-center gap-1">
                  {k.value && <button onClick={() => copy(k.value)} className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5" aria-label="Copy"><Copy className="w-4 h-4" /></button>}
                  {isOwner && <button onClick={() => toggleVisible(k)} className="px-2.5 py-1 rounded-full border border-white/10 text-[11px] text-white/70 hover:bg-white/5">{k.admin_visible ? 'Make private' : 'Share w/ admins'}</button>}
                  {isOwner && <button onClick={() => del(k)} className="p-2 rounded-md text-white/55 hover:text-[#ff6b8a] hover:bg-[#ff2e63]/10" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { setRows(await adminAudit({ limit: 200 })); } catch { toast.error('Load failed'); } setLoading(false); };
  useEffect(() => { load(); }, []);

  if (loading) return <LoaderBlock />;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="font-display text-[18px]">Audit log</h3>
          <p className="text-[12.5px] text-white/55 mt-1">Last 200 admin actions, newest first.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={adminToursCsvUrl()} target="_blank" rel="noreferrer" className="btn-ghost text-[12.5px]"><Download className="w-3.5 h-3.5" /> Export leads CSV</a>
          <button onClick={load} className="btn-ghost text-[12.5px]"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>
      {rows.length === 0 ? <Empty label="No admin actions logged yet." /> : (
        <div className="card-shell overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                  <th className="text-left px-3 py-2 font-semibold">Actor</th>
                  <th className="text-left px-3 py-2 font-semibold">Action</th>
                  <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Target</th>
                  <th className="text-left px-4 py-2 font-semibold hidden lg:table-cell">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-white/5" data-testid={`audit-row-${r.id}`}>
                    <td className="px-4 py-2 text-white/70 whitespace-nowrap"><Clock className="w-3 h-3 inline mr-1 text-white/40" />{(r.at || '').replace('T', ' ').slice(0, 19)}</td>
                    <td className="px-3 py-2 text-white/75"><span className="capitalize text-[11px] uppercase tracking-wider text-[#a78bfa] mr-1">{r.actor_role}</span>{r.actor_email}</td>
                    <td className="px-3 py-2"><span className="chip text-[10px]">{r.action}</span></td>
                    <td className="px-3 py-2 hidden md:table-cell text-white/60">{r.target_kind}{r.target_id ? ` · ${String(r.target_id).slice(0, 10)}` : ''}</td>
                    <td className="px-4 py-2 hidden lg:table-cell text-white/55 text-[11.5px] truncate max-w-[340px]">{r.meta ? JSON.stringify(r.meta) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ——————————————————————————————————————————————————————————————
function AnnounceTab() {
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const r = await getAnnouncement(); setMsg(r.message || ''); } finally { setLoading(false); } })(); }, []);

  const publish = async () => { try { await updateAnnouncement(msg); toast.success('Announcement published site-wide.'); } catch { toast.error('Failed'); } };
  const clear = async () => { try { await updateAnnouncement(''); setMsg(''); toast('Cleared'); } catch {} };

  return (
    <div className="card-shell p-5 max-w-[720px]">
      <div className="flex items-center gap-2"><Bell className="w-4 h-4 text-[#ffcf52]" /><h3 className="font-display text-[18px]">Platform announcement</h3></div>
      <p className="text-white/60 text-[13px] mt-1">Shows at the top of every authenticated page. Use sparingly — emergencies or launches only.</p>
      <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={3} disabled={loading} placeholder="e.g. Scheduled maintenance tonight 02:00 UTC."
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#ffcf52]/60 mt-3" data-testid="announce-input" />
      <div className="flex gap-2 mt-3">
        <button onClick={publish} className="btn-primary text-[12.5px]" data-testid="announce-publish">Publish</button>
        <button onClick={clear} className="btn-ghost text-[12.5px]">Clear</button>
      </div>
    </div>
  );
}

function LoaderBlock() { return <div className="flex items-center gap-2 text-white/60 py-16 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }
function Empty({ label }) { return <div className="text-white/45 text-[13px] py-8 text-center">{label}</div>; }
